/**
 * VerifyEmail Page - Handles magic link email verification
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { useAuthStore } from '../store';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuthStore();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Link de verificación inválido');
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const res = await api.get(`/auth/email/verify-magic-link?token=${token}`);
      
      if (res.data.success) {
        setStatus('success');
        setMessage(res.data.message || '¡Correo verificado!');
        setEmail(res.data.email || '');
        
        // Refresh user data in store
        try {
          await refreshUser();
        } catch (e) {
          // User might not be logged in, that's ok
        }
      } else {
        setStatus('error');
        setMessage(res.data.detail || 'No se pudo verificar el correo');
      }
    } catch (err) {
      setStatus('error');
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setMessage(detail);
      } else if (Array.isArray(detail)) {
        setMessage(detail[0]?.msg || 'Error al verificar');
      } else {
        setMessage('Link inválido o expirado');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md text-center"
      >
        {status === 'loading' && (
          <div className="space-y-4" data-testid="verify-email-loading-state">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-bold">Verificando...</h1>
            <p className="text-muted-foreground">Un momento, estamos verificando tu correo</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6" data-testid="verify-email-success-state">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mx-auto"
            >
              <CheckCircle className="w-12 h-12 text-green-500" />
            </motion.div>
            
            <div>
              <h1 className="text-2xl font-bold text-green-500 mb-2">¡Verificado!</h1>
              <p className="text-muted-foreground" data-testid="verify-email-success-message">{message}</p>
              {email && (
                <p className="text-sm text-primary mt-2 font-medium" data-testid="verify-email-address">{email}</p>
              )}
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <p className="text-sm text-primary font-medium">
                🎉 Tus videos ahora se guardan por <strong>72 horas</strong>
              </p>
            </div>

            <Button
              onClick={() => navigate('/home')}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="verify-email-success-home-button"
            >
              Ir al inicio
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6" data-testid="verify-email-error-state">
            <div className="w-24 h-24 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            
            <div>
              <h1 className="text-2xl font-bold text-red-500 mb-2">Error</h1>
              <p className="text-muted-foreground" data-testid="verify-email-error-message">{message}</p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/profile')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Mail className="w-4 h-4 mr-2" />
                Solicitar nuevo link
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/home')}
                className="w-full"
                data-testid="verify-email-error-home-button"
              >
                Ir al inicio
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
